import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { IconUpload } from "@tabler/icons-react";

const AttendanceImportModal = ({ open, onClose, onImported }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const reset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx")) {
      setImportResult({
        success: false,
        error: "Selecione um arquivo Excel .xlsx.",
      });
      event.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch("/backend/attendances/import-xlsx/", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const contentType = response.headers.get("content-type") || "";
      let result;
      try {
        result = contentType.includes("application/json")
          ? await response.json()
          : { error: `Servidor retornou ${response.status}. Verifique se está logado.` };
      } catch {
        result = {
          error:
            response.status === 401 || response.status === 403
              ? "Sessão expirada ou sem permissão para importar."
              : "Resposta inválida do servidor. Tente novamente.",
        };
      }

      if (response.ok || response.status === 207) {
        setImportResult(result);
        onImported?.();
      } else {
        setImportResult({
          success: false,
          error: result.error || "Não foi possível importar a planilha.",
          found_headers: result.found_headers,
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        error: error.message || "Não foi possível importar a planilha.",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar atendimentos</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Envie a planilha de atendimentos (.xlsx) com as colunas Identificador, Data do
            Atendimento, Nome da Situação, Nome do Assunto, Nome dos Clientes, Nome dos
            Responsáveis, Observação e Data da Última atualização. Consulta vira informação de
            processo; os demais assuntos vão para Outros. Ativo fica em andamento e Suspenso fica
            cancelado. Cliente que ainda não existe é cadastrado. Reimportar o arquivo atualiza os
            atendimentos que já vieram da planilha. Se o número pertence a um atendimento criado no
            sistema, a linha entra com o próximo identificador livre e a observação guarda o número
            da planilha.
          </Typography>

          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: "none" }}
          />

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<IconUpload />}
            disabled={importing}
            sx={{ mb: 2 }}
          >
            Selecionar planilha
          </Button>

          {selectedFile && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                Arquivo selecionado: <strong>{selectedFile.name}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          )}

          {importing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Importando planilha...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {importResult && (
            <Box sx={{ mt: 2 }}>
              {importResult.success ? (
                <Alert
                  severity={
                    importResult.total_errors > 0 || importResult.renumbered > 0 ? "warning" : "success"
                  }
                >
                  <Typography variant="body2">
                    <strong>Importação concluída.</strong>
                  </Typography>
                  <Typography variant="body2">• Criados: {importResult.created} atendimentos</Typography>
                  <Typography variant="body2">• Atualizados: {importResult.updated || 0}</Typography>
                  <Typography variant="body2">
                    • Clientes criados: {importResult.customers_created}
                  </Typography>
                  {importResult.skipped > 0 && (
                    <Typography variant="body2">
                      • Já importados: {importResult.skipped}
                    </Typography>
                  )}
                  {importResult.renumbered > 0 && (
                    <Typography variant="body2">
                      • Novo número (já existia no sistema): {importResult.renumbered}
                    </Typography>
                  )}
                  {importResult.total_errors > 0 && (
                    <Typography variant="body2">• Erros: {importResult.total_errors} linhas</Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Erro ao importar a planilha.</strong>
                  </Typography>
                  <Typography variant="body2">{importResult.error}</Typography>
                  {importResult.found_headers && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Cabeçalhos encontrados: {importResult.found_headers.join(", ")}
                    </Typography>
                  )}
                </Alert>
              )}

              {importResult.renumbered_items?.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 160, overflow: "auto" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Números ajustados:</strong>
                  </Typography>
                  {importResult.renumbered_items.map((item) => (
                    <Typography key={item} variant="caption" display="block">
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}

              {importResult.errors?.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Detalhes dos erros:</strong>
                  </Typography>
                  {importResult.errors.map((error, index) => (
                    <Typography key={index} variant="caption" color="error" display="block">
                      {error}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={importing}>
          Fechar
        </Button>
        <Button
          onClick={handleImport}
          color="primary"
          disabled={!selectedFile || importing}
          startIcon={importing ? null : <IconUpload />}
        >
          {importing ? "Importando..." : "Importar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceImportModal;
